import { DateTime } from 'luxon';
import { object } from 'prop-types';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useNotifications } from '~/providers/notifications';
import { useUser } from '~/providers/user';
import notificationEventTypes from '~/settings/notificationEventTypes';
import { formatTagsToHTML } from '~/utils/formatTaggedUsers';
import { getBackgroundColor, getUserInitials } from '~/utils/users';
const FileCompletedNotification = ({ notification }) => {
    FileCompletedNotification.propTypes = {
        notification: object.isRequired,
    };
    const { value, payload } = notification;
    const type = notification.notificationEventType.name;
    const datestamp = DateTime.fromISO(`${notification.datetime}Z`);
    const dateDisplay = datestamp.toRelative();
    const dateTitle = datestamp.toLocaleString(DateTime.DATETIME_MED);
    return React.createElement(React.Fragment, null,
        React.createElement("h3", null,
            React.createElement("div", null,
                type,
                " \u2022 ",
                React.createElement("span", { className: '', title: dateTitle }, dateDisplay))),
        React.createElement("div", { className: "content" },
            value,
            payload &&
                React.createElement("div", { className: 'notification-link' }, payload.fileName && React.createElement(Link, { to: `/document/${payload.fileId}` },
                    React.createElement("i", { className: 'fas fa-file-upload icon' }),
                    " ",
                    payload.fileName))));
};
export const TaggedUserComparisonCommentNotification = ({ notification }) => {
    TaggedUserComparisonCommentNotification.propTypes = {
        notification: object.isRequired,
    };
    const { taggableComparisonUsers } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const { payload } = notification;
    const comment = payload.comment;
    const datestamp = DateTime.fromISO(`${notification.datetime}Z`);
    const dateDisplay = datestamp.toRelative();
    const dateTitle = datestamp.toLocaleString(DateTime.DATETIME_MED);
    const userInitials = getUserInitials(comment.user);
    const routeToDocumentComparison = () => {
        const redirectPath = `/compare/${payload.documentComparisonId}/${comment.id}`;
        if (location.pathname.includes(`/compare/${payload.documentComparisonId}`)) {
            navigate(redirectPath);
            window.location.reload();
            return;
        }
        navigate(redirectPath);
    };
    return React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'content' },
            React.createElement("span", { className: 'user-bubble', style: { backgroundColor: getBackgroundColor(userInitials) } }, userInitials),
            React.createElement("span", { className: 'comment', onClick: routeToDocumentComparison },
                React.createElement("h6", null,
                    React.createElement("div", null,
                        React.createElement("b", null, "New message"),
                        " \u2022 ",
                        React.createElement("span", { className: '', title: dateTitle }, dateDisplay))),
                React.createElement("p", { dangerouslySetInnerHTML: {
                        __html: formatTagsToHTML(comment.text, taggableComparisonUsers),
                    } }))));
};
const ReadUnreadButton = ({ notification }) => {
    ReadUnreadButton.propTypes = {
        notification: object.isRequired,
    };
    let readIcon, unreadIcon;
    switch (notification.notificationEventType.name) {
        case notificationEventTypes.TaggedUserComparisonComment:
            readIcon = 'fas fa-comment';
            unreadIcon = 'fas fa-comment';
            break;
        case notificationEventTypes.FileCompleted:
        default:
            readIcon = 'fas fa-envelope-open';
            unreadIcon = 'fas fa-envelope';
    }
    const { isRead, id } = notification;
    const { markAsRead, markAsUnread, updatingNotificationId } = useNotifications();
    const updatingNotification = updatingNotificationId === id;
    const handleMarkReadStatus = (notification) => {
        if (notification.isRead) {
            markAsUnread(notification.id);
            return;
        }
        markAsRead(notification.id);
    };
    return React.createElement("div", { className: 'is-read', onClick: () => handleMarkReadStatus(notification) },
        updatingNotification &&
            React.createElement("span", { title: 'Updating Notification' },
                React.createElement("i", { className: 'fas fa-circle-notch icon fa-spin' })),
        !updatingNotification && isRead &&
            React.createElement("button", { title: 'Mark as Unread' },
                React.createElement("i", { className: `${readIcon} icon read` })),
        !updatingNotification && !isRead &&
            React.createElement("button", { title: 'Mark as Read' },
                React.createElement("i", { className: `${unreadIcon} icon unread` })));
};
const Notification = ({ notification }) => {
    Notification.propTypes = {
        notification: object.isRequired,
    };
    const notificationContent = () => {
        switch (notification.notificationEventType.name) {
            case notificationEventTypes.TaggedUserComparisonComment:
                return React.createElement(TaggedUserComparisonCommentNotification, { notification: notification });
            case notificationEventTypes.FileCompleted:
            default:
                return React.createElement(FileCompletedNotification, { notification: notification });
        }
    };
    return React.createElement("div", { className: 'notification', key: notification.id },
        notificationContent(),
        React.createElement(ReadUnreadButton, { notification: notification }));
};
export default Notification;
